import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Observable, of, Subject} from 'rxjs';
import {SessionService} from '../session.service';
import {ConstantsService} from '../constants.service';
import {PolicyService} from "../policy.service";
import {EventDispatcherService} from "../shared/services/event-dispatcher/event-dispatcher.service";

type LoginSuccessResponse = {
  user?: LoginUserResponse;
} & Partial<LoginUserResponse>;

type LoginUserResponse = {
  userId: string,
  clientId?: string | null,
  entityId?: string | null,
  entityType?: string | null,
  name?: string | null,
  username?: string | null,
  email?: string | null
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private policyIdsLoadPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private constantService: ConstantsService,
    private policyService: PolicyService,
    private eventDispatcher: EventDispatcherService,
  ) {
    this.startExpiredSessionWatcher();
  }

  login(username: string, password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({'Content-Type': 'application/json'});
      const body = JSON.stringify({username, password});

      this.constantService.onReady(() => {
        return this.http
          .post<LoginSuccessResponse>(`${this.constantService.getConstant('apiUrl')}/user/api/v2/auth/login`, body, {
            headers: headers,
            withCredentials: true
          })
          .subscribe({
            next: async (response) => {
              await this.handleLoginSuccessResponse(response)

              resolve(response);
            },
            error: (error: HttpErrorResponse) => {
              reject(error)
            }
          })
      })
    });
  }

  async handleLoginSuccessResponse(response: LoginSuccessResponse): Promise<void> {
    const user = response?.user ?? this.extractUserFromTopLevelResponse(response);

    if (user) {
      this.sessionService.setValue('user_id', user.userId);
      this.sessionService.setValue('client_id', user.clientId ?? '');
      this.sessionService.setValue('entity_id', user.entityId ?? '');
      this.sessionService.setValue('entity_type', user.entityType ?? '');
      this.sessionService.setValue('name', user.name ?? '');
      this.sessionService.setValue('username', user.username ?? '');
      this.sessionService.setValue('email', user.email ?? '');
    }

    await this.ensurePolicyIdsLoaded(true);
  }

  private extractUserFromTopLevelResponse(response: LoginSuccessResponse): LoginUserResponse | null {
    if (
      response?.userId &&
      (response?.clientId || response?.email || response?.username)
    ) {
      return {
        userId: response.userId,
        clientId: response.clientId ?? '',
        entityId: response.entityId ?? '',
        entityType: response.entityType ?? '',
        name: response.name ?? '',
        username: response.username ?? '',
        email: response.email ?? '',
      };
    }

    return null;
  }

  async ensurePolicyIdsLoaded(forceReload = false): Promise<void> {
    const userId = this.sessionService.getValue('user_id');
    if (!userId) {
      return;
    }

    const storedPolicyIds = this.sessionService.getValue('policy_ids');
    const hasPolicyIds = Array.isArray(storedPolicyIds) && storedPolicyIds.length > 0;
    if (hasPolicyIds && !forceReload) {
      return;
    }

    if (forceReload) {
      this.policyIdsLoadPromise = null;
    } else if (this.policyIdsLoadPromise) {
      return this.policyIdsLoadPromise;
    }

    this.policyIdsLoadPromise = this.policyService
      .getPolicyIds()
      .then((policyIds) => {
        this.sessionService.setValue(
          'policy_ids',
          Array.isArray(policyIds) ? policyIds : []
        );
      })
      .catch((error) => {
        console.error('Unable to load policy ids for active session:', error);
        this.sessionService.setValue('policy_ids', []);
      })
      .finally(() => {
        this.policyIdsLoadPromise = null;
      });

    return this.policyIdsLoadPromise;
  }

  loginWithMfa(token: string, otp: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({'Content-Type': 'application/json'});
      const body = JSON.stringify({mfaToken: token, otpCode: otp});

      this.constantService.onReady(() => {
        return this.http
          .post<LoginSuccessResponse>(
            `${this.constantService.getConstant('apiUrl')}/user/api/v2/auth/mfa-verify-otp`,
            body,
            {
              headers: headers,
              withCredentials: true
            }
          )
          .subscribe({
            next: async (response) => {
              await this.handleLoginSuccessResponse(response)

              resolve(response);
            },
            error: (error: HttpErrorResponse) => {
              reject(error)
            }
          })
      })
    });
  }

  checkSession(): Observable<boolean> {
    return of(this.sessionService.getValue('user_id') != null);
  }

  startExpiredSessionWatcher() {
    window.setInterval(() => {
      this.checkSession().subscribe({
        next: (isAuthenticated) => {
          if (!isAuthenticated) {
            // Don't redirect if user is on a public route (guest, login, forgot-password, etc.)
            if (!this.isPublicRoute(location.pathname)) {
              location.href = '/login';
            }
          } else {
            if (location.pathname.includes('/login')) {
              location.href = '/';
            }
          }
        }
      });
    }, 5000);
  }

  isPublicRoute(path: string): boolean {
    // Accept router URLs with query, fragment and matrix parameters without
    // treating a public-route name inside a private URL as an exemption.
    const pathname = path.split(/[?#]/)[0].replace(/;[^/]*/g, '').replace(/\/$/, '');
    const publicRoutes = ['/login', '/forgot-password', '/password-reset-sent', '/pwd-recovery', '/mfa'];
    return publicRoutes.includes(pathname) || pathname === '/guest' || pathname.startsWith('/guest/');
  }

  private logoutSubject = new Subject<any>();

  logout(): Observable<any> {
    this.constantService.onReady(() => {
      this.http.post<any>(`${this.constantService.getConstant('apiUrl')}/user/api/auth/logout`, {}, {
        responseType: 'text' as 'json',
        withCredentials: true
      }).subscribe({
        next: (response) => {
          this.sessionService.clear();
          this.logoutSubject.next(response);

          this.eventDispatcher.dispatchEvent('logout', {});
        },
        error: (error) => {
          this.sessionService.clear();
          // the session is cleared locally even if the API call failed, so listeners
          // (help panel, websocket, ...) must hear about the logout as well
          this.eventDispatcher.dispatchEvent('logout', {});
        }
      });
    })

    return this.logoutSubject.asObservable();
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unable to complete your request. Please try again later.';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `An error occurred: ${error.error.message}`;
    } else {
      // Server-side errors
      switch (error.status) {
        case 401:
          errorMessage = 'Incorrect username or password. Please check your credentials and try again.';
          break;
        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'The requested resource was not found.';
          break;
        case 500:
          errorMessage = 'We are experiencing server issues. Please try again later.';
          break;
        case 423:
          errorMessage = 'Your account is locked. Please click on Forgot Password to reset your password.';
          break;
        default:
          errorMessage = `Unexpected error occurred: ${error.message}`;
          break;
      }
    }
    console.error('Authentication service encountered an error:', errorMessage);

    return errorMessage;
  }
}
